import type {
  ObjectActionContext,
  ObjectActionPredicate,
  ObjectKind,
  ObjectKindFilter,
} from "./types";

export function evaluatePredicate(
  predicate: ObjectActionPredicate | undefined,
  context: ObjectActionContext,
): boolean {
  if (!predicate) {
    return false;
  }

  if ("not" in predicate) {
    return !evaluatePredicate(predicate.not, context);
  }
  if ("all" in predicate) {
    return predicate.all.every((p) => evaluatePredicate(p, context));
  }
  if ("any" in predicate) {
    return predicate.any.some((p) => evaluatePredicate(p, context));
  }
  if ("permission" in predicate) {
    return context.permissions[predicate.permission] === true;
  }
  if ("scope" in predicate) {
    return context.scope === predicate.scope;
  }
  if ("state" in predicate) {
    return context.state[predicate.state] === true;
  }
  if ("kind" in predicate) {
    return matchesKindFilter(context.kind, predicate.kind);
  }

  return false;
}

export function isHidden(
  hiddenWhen: ObjectActionPredicate | undefined,
  context: ObjectActionContext,
): boolean {
  return evaluatePredicate(hiddenWhen, context);
}

export function isDisabled(
  disabledWhen: ObjectActionPredicate | undefined,
  context: ObjectActionContext,
): boolean {
  return evaluatePredicate(disabledWhen, context);
}

function matchesKindFilter(
  kind: ObjectKind,
  filter: Partial<ObjectKindFilter>,
): boolean {
  if (filter.type !== undefined && kind.type !== filter.type) {
    return false;
  }
  if (kind.type === "workspace" && filter.scope !== undefined) {
    return kind.scope === filter.scope;
  }
  if (kind.type === "file-record" && filter.isFolder !== undefined) {
    return kind.isFolder === filter.isFolder;
  }
  if (kind.type === "canvas-node") {
    if (filter.nodeType !== undefined && kind.nodeType !== filter.nodeType) {
      return false;
    }
    if (
      filter.entityBase !== undefined &&
      kind.entityBase !== filter.entityBase
    ) {
      return false;
    }
  }
  return true;
}

/** Shared manifest predicates */
export const predicates = {
  privateWorkspace: {
    kind: { type: "workspace", scope: "private" },
  } satisfies ObjectActionPredicate,
  sharedWithMeScope: {
    scope: "shared-with-me",
  } satisfies ObjectActionPredicate,
  favoritesScope: { scope: "favorites" } satisfies ObjectActionPredicate,
  copyDisabled: { state: "copyDisabled" } satisfies ObjectActionPredicate,
  zOrderDisabled: { state: "zOrderDisabled" } satisfies ObjectActionPredicate,
  cannotShare: { not: { state: "canShare" } } satisfies ObjectActionPredicate,
  cannotDelete: { not: { state: "canDelete" } } satisfies ObjectActionPredicate,
  cannotEdit: { not: { state: "canEdit" } } satisfies ObjectActionPredicate,
  isFavorite: { state: "isFavorite" } satisfies ObjectActionPredicate,
  /** Hide unless surfaced somewhere files can be uploaded (files tab containers). */
  notFileUploadContext: {
    not: { state: "canUploadFiles" },
  } satisfies ObjectActionPredicate,
  isSkillEnabled: { state: "isSkillEnabled" } satisfies ObjectActionPredicate,
  cannotUnpublish: {
    not: { state: "canUnpublish" },
  } satisfies ObjectActionPredicate,
};
